import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { getInvoice } from "../../../redux/reducers/Properties/PropertiesSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { currencySymbol } from "shared/CurrencySymbol";
import { InvoiceData } from "../../../redux/reducers/Properties/PropertiesSliceTypes";
import CustomDatePickerV2 from "shared/DateField/CustomDatePickerV2";
import Skeleton from "react-loading-skeleton";
import moment from "moment";
import Pagination from "shared/Pagination/Pagination";
import { useNavigate } from "react-router-dom";

interface TotalItems {
  invoice_amt: string;
  confirmed_amt: string;
  host_net_confirmed_amt?: string;
  host_net_invoice_amt?: string;
  host_confirmed_amt?: string; // ✅ NEW: sum of monthly_rent only, no fee deductions
}

interface InvoiceDataExtended extends InvoiceData {
  paid_at?: string;
  payment_method_name?: string;
  transaction_id?: string;
}

const formatPaidAt = (paidAt: string): string =>
  moment.utc(paidAt, "MMMM DD, YYYY [at] hh:mm A").format("MMM DD, YYYY");

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; classes: string }> = {
    paid:      { label: "Paid",      classes: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
    pending:   { label: "Pending",   classes: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
    cancelled: { label: "Cancelled", classes: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400" },
    refunded:  { label: "Refunded",  classes: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" },
  };
  const cfg = map[status] ?? map["pending"];
  return (
    <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.classes}`}>
      {cfg.label}
    </span>
  );
};

// ─── InvoiceDetailModal ───────────────────────────────────────────────────────

interface InvoiceDetailModalProps {
  isOpen: boolean;
  invoice: InvoiceDataExtended | null;
  onClose: () => void;
  onViewBooking: (bookingId: number) => void;
  isHost: boolean;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onViewBooking,
  isHost,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const isUnpaid = invoice?.status === "pending" || invoice?.status === "cancelled";

  const statusBanner = () => {
    if (invoice?.status === "cancelled") {
      return (
        <div className="px-4 py-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
          <div className="flex items-center gap-3">
            <span className="text-2xl">❌</span>
            <div>
              <p className="text-sm font-bold text-gray-700 dark:text-gray-300">Invoice Cancelled</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">This invoice was cancelled</p>
            </div>
          </div>
        </div>
      );
    }
    if (invoice?.status === "pending") {
      return (
        <div className="px-4 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⏳</span>
            <div>
              <p className="text-sm font-bold text-blue-700 dark:text-blue-300">Payment Pending</p>
              <p className="text-xs text-blue-500 dark:text-blue-400 mt-0.5">This invoice hasn't been paid yet</p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return createPortal(
    <div
      aria-modal="true"
      role="dialog"
      className="items-end md:items-center"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? "auto" : "none",
        transition: "opacity 150ms ease",
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "32rem",
          maxHeight: "92vh",
          overflowY: "auto",
          transform: isOpen ? "translateY(0)" : "translateY(20px)",
          transition: "transform 150ms ease",
          borderRadius: "1rem 1rem 0 0",
        }}
        className="bg-white dark:bg-gray-900 md:rounded-2xl md:mb-8"
      >
        {/* Close button */}
        <button
          type="button"
          aria-label="Close"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            zIndex: 20,
            width: "2.25rem",
            height: "2.25rem",
            borderRadius: "50%",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            flexShrink: 0,
          }}
          className="dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="2" y1="2" x2="14" y2="14" />
            <line x1="14" y1="2" x2="2" y2="14" />
          </svg>
        </button>

        {/* Drag handle for mobile */}
        <div className="flex justify-center pt-3 pb-1 md:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>

        {invoice && (
          <>
            {/* Header */}
            <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 px-6 py-6 text-white overflow-hidden rounded-t-2xl">
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-24 -mt-24 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-36 h-36 bg-white/5 rounded-full -ml-18 -mb-18 pointer-events-none" />
              <div className="relative z-10 flex items-center justify-between pr-10">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold tracking-tight">Invoice #{invoice.id}</h2>
                    <p className="text-blue-100 text-xs mt-0.5">{invoice.invoice_type}</p>
                  </div>
                </div>
                <StatusBadge status={invoice.status as string} />
              </div>
            </div>

            {/* Body */}
            <div className="px-5 py-5 space-y-5">
              {/* Invoice Info */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
                <h3 className="text-xs font-bold text-gray-900 dark:text-gray-100 mb-3 uppercase tracking-wide">
                  Invoice Information
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Issue Date</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {moment.utc(invoice.created_at).local().format("MMM DD, YYYY")}
                    </p>
                  </div>
                  {invoice.paid_at && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Payment Date</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatPaidAt(invoice.paid_at)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Period</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{invoice.invoice_period}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Guests</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{invoice.number_of_guests}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Billing</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{invoice.billing_type}</p>
                  </div>
                  {invoice.no_of_months && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Duration</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {invoice.no_of_months} {invoice.no_of_months === 1 ? "month" : "months"}
                      </p>
                    </div>
                  )}
                  {invoice.status === "paid" && invoice.payment_method_name && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Payment Method</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{invoice.payment_method_name}</p>
                    </div>
                  )}
                  {invoice.status === "paid" && invoice.transaction_id && (
                    <div className="col-span-2">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Transaction ID</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-all font-mono">{invoice.transaction_id}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Breakdown */}
              <div>
                <h3 className="text-xs font-bold text-gray-900 dark:text-gray-100 mb-3 uppercase tracking-wide">
                  Price Breakdown
                </h3>
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">

                    {isUnpaid ? (
                      /* ── PENDING / CANCELLED — no calculations ── */
                      statusBanner()
                    ) : isHost ? (
                      /* ── HOST PAID VIEW — monthly rent only, no fee deductions ── */
                      <>
                        <div className="flex justify-between items-center px-4 py-3">
                          <span className="text-sm text-gray-700 dark:text-gray-300">First month's rent</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {`${currencySymbol()} ${Number(invoice.first_month_price)?.toFixed(2)}`}
                          </span>
                        </div>
                        <div className="px-4 py-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30">
                          <div className="flex justify-between items-center">
                            <span className="text-base font-bold text-gray-900 dark:text-gray-100">💰 Your Net Payout</span>
                            <span className="text-xl font-bold text-green-600 dark:text-green-400">
                              {`${currencySymbol()} ${Number(invoice.first_month_price)?.toFixed(2)}`}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      /* ── TENANT/STUDENT PAID VIEW ── */
                      <>
                        <div className="flex justify-between items-center px-4 py-3">
                          <span className="text-sm text-gray-700 dark:text-gray-300">First month's rent</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {`${currencySymbol()} ${Number(invoice.first_month_price)?.toFixed(2)}`}
                          </span>
                        </div>
                        {invoice.service_tax && Number(invoice.service_tax) > 0 && (
                          <div className="flex justify-between items-center px-4 py-3">
                            <span className="text-sm text-gray-700 dark:text-gray-300">Service Fee</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {`${currencySymbol()} ${Number(invoice.service_tax)?.toFixed(2)}`}
                            </span>
                          </div>
                        )}
                        {invoice.extra_guest_amount && Number(invoice.extra_guest_amount) > 0 && (
                          <div className="flex justify-between items-center px-4 py-3">
                            <span className="text-sm text-gray-700 dark:text-gray-300">Extra guest</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {`${currencySymbol()} ${Number(invoice.extra_guest_amount)?.toFixed(2)}`}
                            </span>
                          </div>
                        )}
                        {invoice.expenses?.length > 0 && invoice.expenses.map((expense: any, i: number) => (
                          <div key={`exp-${i}`} className="flex justify-between items-center px-4 py-3">
                            <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{expense.category}</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {`${currencySymbol()} ${Number(expense.amount).toFixed(2)}`}
                            </span>
                          </div>
                        ))}
                        {invoice.discounts?.length > 0 && invoice.discounts.map((discount: any, i: number) => (
                          <div key={`dis-${i}`} className="flex justify-between items-center px-4 py-3 bg-green-50 dark:bg-green-900/20">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-600 text-white">DISCOUNT</span>
                              <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{discount.description}</span>
                            </div>
                            <span className="text-sm font-bold text-green-600 dark:text-green-400 ml-2">
                              -{`${currencySymbol()} ${Number(discount.amount).toFixed(2)}`}
                            </span>
                          </div>
                        ))}
                        <div className="px-4 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
                          <div className="flex justify-between items-center">
                            <span className="text-base font-bold text-gray-900 dark:text-gray-100">Total Amount</span>
                            <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                              {`${currencySymbol()} ${Number(invoice.total)?.toFixed(2)}`}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onMouseDown={() => onViewBooking(invoice.booking)}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors text-sm"
              >
                View Booking
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

// ─── Mobile Invoice Card ──────────────────────────────────────────────────────

const InvoiceCard: React.FC<{
  inv: InvoiceDataExtended;
  isHost: boolean;
  onOpen: (inv: InvoiceDataExtended) => void;
  onViewBooking: (id: number) => void;
}> = ({ inv, isHost, onOpen, onViewBooking }) => (
  <div
    className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
    onClick={() => onOpen(inv)}
  >
    <div className="flex items-start justify-between mb-3">
      <div>
        <span className="text-blue-600 dark:text-blue-400 font-bold text-base">#{inv.id}</span>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{inv.invoice_type} · {inv.billing_type}</p>
      </div>
      <StatusBadge status={inv.status as string} />
    </div>
    <div className="grid grid-cols-2 gap-2 mb-3">
      <div>
        <p className="text-xs text-gray-400 dark:text-gray-500">Issue Date</p>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {moment.utc(inv.created_at).format("MMM DD, YYYY")}
        </p>
      </div>
      <div>
        <p className="text-xs text-gray-400 dark:text-gray-500">Payment Date</p>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {inv.paid_at ? formatPaidAt(inv.paid_at) : <span className="text-gray-400">—</span>}
        </p>
      </div>
    </div>
    <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
      <div>
        {inv.status === "pending" ? (
          <p className="text-sm font-semibold text-blue-500 dark:text-blue-400">⏳ Pending</p>
        ) : inv.status === "cancelled" ? (
          <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">❌ Cancelled</p>
        ) : (
          <>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{isHost ? "Net Payout" : "Amount"}</p>
            <p className={`text-lg font-bold ${isHost ? "text-green-600 dark:text-green-400" : "text-gray-900 dark:text-gray-100"}`}>
              {isHost
                ? `${currencySymbol()} ${Number(inv.first_month_price).toFixed(2)}`
                : `${currencySymbol()} ${Number(inv.total).toFixed(2)}`}
            </p>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onViewBooking(inv.booking); }}
        className="text-xs text-blue-600 dark:text-blue-400 font-semibold border border-blue-200 dark:border-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
      >
        View Booking
      </button>
    </div>
  </div>
);

// ─── Main Invoice Page ────────────────────────────────────────────────────────

const Invoice: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useSelector((state: RootState) => state.user);
  const isHost = currentUser?.is_host === true;
  const [invoiceList, setInvoiceList] = useState<InvoiceDataExtended[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDataExtended | null>(null);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    invoiceType: "Reservation fee",
    status: "Any",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState<TotalItems | null>(null);
  const navigate = useNavigate();

  const fetchInvoices = useCallback(
    async (pageNum = 1) => {
      setLoading(true);
      setError(null);
      setInvoiceList([]);
      try {
        const response = await dispatch(
          getInvoice({
            start_date: filters.fromDate,
            end_date: filters.toDate,
            type: filters.invoiceType,
            status: filters.status === "Any" ? undefined : filters.status,
            page: pageNum,
          })
        ).unwrap();
        if (response?.success) {
          setInvoiceList(response.data);
          // ✅ FIX: Only update summary totals on page 1 (fresh filter fetch)
          if (pageNum === 1) {
            setTotalItems(response);
          }
          setTotalPages(response?.meta?.total_pages || 1);
        } else {
          setError(response?.error || "No invoices found.");
        }
      } catch (err: any) {
        setError(err.response?.data?.error || err.message || "Failed to fetch invoices.");
      } finally {
        setLoading(false);
      }
    },
    [dispatch, filters]
  );

  useEffect(() => {
    setPage(1);
    fetchInvoices(1);
  }, [filters]);

  const openModal = (inv: InvoiceDataExtended) => {
    setSelectedInvoice(inv);
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedInvoice(null), 200);
  }, []);

  const handleViewBooking = useCallback(
    (bookingId: number) => {
      setIsModalOpen(false);
      navigate(`/booking_request?id=${bookingId}`);
    },
    [navigate]
  );

  return (
    <div className="p-4 md:p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 min-h-screen">
      <div className="container mx-auto max-w-7xl">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent mb-1">
            Invoices
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm">Manage and track all your invoices</p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 mb-5">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wide">From Date</label>
              <CustomDatePickerV2
                placeholder="Start date"
                value={filters.fromDate ? new Date(filters.fromDate) : null}
                onChange={(date: Date | null) =>
                  setFilters({
                    ...filters,
                    fromDate: date ? date.toLocaleDateString("sv-SE") : "",
                    toDate: date && filters.toDate && new Date(filters.toDate) < date ? "" : filters.toDate,
                  })
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wide">To Date</label>
              <CustomDatePickerV2
                placeholder="End date"
                value={filters.toDate ? new Date(filters.toDate) : null}
                minDate={filters.fromDate ? moment(filters.fromDate).add(1, "day").toDate() : new Date()}
                onChange={(date: Date | null) =>
                  setFilters({ ...filters, toDate: date ? date.toLocaleDateString("sv-SE") : "" })
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wide">Type</label>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 p-2.5 rounded-xl bg-white dark:bg-gray-700 dark:text-white cursor-not-allowed opacity-60 text-sm"
                value="Reservation fee"
                disabled
              >
                <option>Reservation fee</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wide">Status</label>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 p-2.5 rounded-xl bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all text-sm"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="Any">Any</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3 md:gap-6 mb-5">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-4 md:p-6 text-white shadow-lg">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-green-100 text-xs md:text-sm font-medium mb-1 md:mb-2">
                  {isHost ? "Net Earnings" : "Total Paid"}
                </p>
                <p className="text-xl md:text-3xl font-bold truncate">
                  {totalItems
                    ? `${currencySymbol()} ${Number(
                        isHost
                          ? (totalItems.host_confirmed_amt ?? totalItems.confirmed_amt)
                          : totalItems.confirmed_amt
                      )?.toFixed(2)}`
                    : "N/A"}
                </p>
              </div>
              <div className="w-9 h-9 md:w-12 md:h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 ml-2">
                <svg className="w-5 h-5 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-4 md:p-6 text-white shadow-lg">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-blue-100 text-xs md:text-sm font-medium mb-1 md:mb-2">
                  {isHost ? "Pending" : "Total Pending"}
                </p>
                <p className="text-xl md:text-3xl font-bold truncate">
                  {totalItems
                    ? `${currencySymbol()} ${Number(isHost ? (totalItems.host_net_invoice_amt ?? totalItems.invoice_amt) : totalItems.invoice_amt)?.toFixed(2)}`
                    : "N/A"}
                </p>
              </div>
              <div className="w-9 h-9 md:w-12 md:h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 ml-2">
                <svg className="w-5 h-5 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* ── MOBILE: Card list (hidden on md+) ── */}
        <div className="md:hidden space-y-3">
          {loading ? (
            Array(4).fill(null).map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
                <Skeleton height={20} className="mb-2" />
                <Skeleton height={16} width="60%" className="mb-3" />
                <Skeleton height={16} count={2} />
              </div>
            ))
          ) : invoiceList.length > 0 ? (
            invoiceList.map((inv) => (
              <InvoiceCard
                key={inv.id}
                inv={inv}
                isHost={isHost}
                onOpen={openModal}
                onViewBooking={(id) => navigate(`/booking_request?id=${id}`)}
              />
            ))
          ) : (
            <div className="py-12 text-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
              <p className="text-gray-500 dark:text-gray-400 font-medium">No invoices found</p>
              <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
              {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            </div>
          )}
          {totalPages > 1 && (
            <div className="pt-2">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(p) => { setPage(p); fetchInvoices(p); }}
              />
            </div>
          )}
        </div>

        {/* ── DESKTOP: Table (hidden below md) ── */}
        <div className="hidden md:block bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 px-6 py-4">
            <div className="grid grid-cols-8 gap-4 font-semibold text-gray-700 dark:text-gray-200 text-sm">
              <div className="text-center">Invoice No.</div>
              <div className="text-center">Issue Date</div>
              <div className="text-center">Payment Date</div>
              <div className="text-center">Type</div>
              <div className="text-center">Billing</div>
              <div className="text-center">Status</div>
              <div className="text-center">Amount</div>
              <div className="text-center">Actions</div>
            </div>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              Array(5).fill(null).map((_, i) => (
                <div key={i} className="grid grid-cols-8 gap-4 px-6 py-5">
                  {Array(8).fill(null).map((_, j) => <Skeleton key={j} height={30} />)}
                </div>
              ))
            ) : invoiceList.length > 0 ? (
              invoiceList.map((inv) => (
                <div
                  key={inv.id}
                  className="grid grid-cols-8 gap-4 px-6 py-5 text-center hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                  onClick={() => openModal(inv)}
                >
                  <div className="font-semibold text-blue-600 dark:text-blue-400">#{inv.id}</div>
                  <div className="text-gray-700 dark:text-gray-300 text-sm">
                    {moment.utc(inv.created_at).format("MMM DD, YYYY")}
                  </div>
                  <div className="text-gray-700 dark:text-gray-300 text-sm">
                    {inv.paid_at ? formatPaidAt(inv.paid_at) : <span className="text-gray-400">-</span>}
                  </div>
                  <div className="text-gray-700 dark:text-gray-300 text-sm">{inv.invoice_type}</div>
                  <div className="text-gray-700 dark:text-gray-300 text-sm">{inv.billing_type}</div>
                  <div className="flex justify-center"><StatusBadge status={inv.status as string} /></div>
                  <div className="flex justify-center items-center">
                    {inv.status === "pending" ? (
                      <span className="text-sm font-semibold text-blue-500 dark:text-blue-400">⏳ Pending</span>
                    ) : inv.status === "cancelled" ? (
                      <span className="text-sm font-semibold text-gray-400 dark:text-gray-500">❌ Cancelled</span>
                    ) : (
                      <span className={`font-semibold ${isHost ? "text-green-600 dark:text-green-400" : "text-gray-900 dark:text-gray-100"}`}>
                        {isHost
                          ? `${currencySymbol()} ${Number(inv.first_month_price).toFixed(2)}`
                          : `${currencySymbol()} ${Number(inv.total).toFixed(2)}`}
                      </span>
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); navigate(`/booking_request?id=${inv.booking}`); }}
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium text-sm transition-colors"
                    >
                      View Booking
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-16 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">No invoices found</p>
                <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <div className="bg-gray-50 dark:bg-gray-700/30 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(p) => { setPage(p); fetchInvoices(p); }}
              />
            </div>
          )}
        </div>

      </div>

      <InvoiceDetailModal
        isOpen={isModalOpen}
        invoice={selectedInvoice}
        onClose={closeModal}
        onViewBooking={handleViewBooking}
        isHost={isHost}
      />
    </div>
  );
};

export default Invoice;